import * as React from 'react'
import { Flex } from '@radix-ui/themes'
import { cn } from '@/lib/utils'
import ArrowButton from '@/components/button/arrow-button'

export interface TablePaginateProps {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
  readOnlyPageDisplay?: boolean
  hideOnSinglePage?: boolean
  disabled?: boolean
  totalItems?: number
  itemPerPage?: number
}

export const TablePaginate: React.FC<TablePaginateProps> = ({
  totalItems,
  itemPerPage,
  page,
  totalPages,
  onPageChange,
  className,
  readOnlyPageDisplay,
  hideOnSinglePage = true,
  disabled = false,
}) => {
  const clampedPage = Math.min(Math.max(page, 1), Math.max(totalPages, 1))
  const [draft, setDraft] = React.useState<string>(String(clampedPage))

  React.useEffect(() => {
    setDraft(String(clampedPage))
  }, [clampedPage])

  if (hideOnSinglePage && totalPages <= 1) return null

  const canPrev = clampedPage > 1 && !disabled
  const canNext = clampedPage < totalPages && !disabled

  function go(to: number) {
    const next = Math.min(Math.max(to, 1), totalPages)
    if (next !== clampedPage) onPageChange(next)
  }

  function handleCommit() {
    const num = Number(draft)
    if (!Number.isNaN(num)) {
      go(num)
    } else {
      setDraft(String(clampedPage))
    }
  }

  const hasCounts =
    typeof totalItems === 'number' && typeof itemPerPage === 'number'
  const startItem = hasCounts
    ? totalItems === 0
      ? 0
      : (clampedPage - 1) * (itemPerPage as number) + 1
    : 0
  const endItem = hasCounts
    ? Math.min(clampedPage * (itemPerPage as number), totalItems as number)
    : 0

  return (
    <nav
      aria-label='Pagination'
      className={cn('flex w-full items-center gap-2 select-none', className)}
    >
      <Flex justify={'between'} align={'center'} className='w-full'>
        <div className=''>
          {hasCounts && `${startItem}-${endItem} of ${totalItems}`}
        </div>
        <div className={cn('flex items-center gap-2 select-none', className)}>
          {clampedPage > 1 && (
            <ArrowButton
              text='Previous'
              direction='left'
              disabled={!canPrev}
              onClick={() => go(clampedPage - 1)}
              className={cn(
                'w-auto px-3',
                !canPrev && 'cursor-not-allowed opacity-40'
              )}
            />
          )}
          <span className='text-sm text-neutral-700'>Page</span>
          {readOnlyPageDisplay ? (
            <span
              className='inline-flex h-9 w-12 items-center justify-center rounded-lg border border-[#EAECF0] bg-white text-sm font-medium'
              aria-current='page'
            >
              {clampedPage}
            </span>
          ) : (
            <input
              type='text'
              value={draft}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/[^0-9]/g, '')
                setDraft(value)
              }}
              onBlur={handleCommit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
                // Prevent non-numeric keys except for navigation keys
                if (
                  !/[0-9]/.test(e.key) &&
                  ![
                    'Backspace',
                    'Delete',
                    'Tab',
                    'Enter',
                    'ArrowLeft',
                    'ArrowRight',
                  ].includes(e.key)
                ) {
                  e.preventDefault()
                }
              }}
              aria-label='Current page'
              className={cn(
                'h-9 w-12 rounded-lg border border-[#EAECF0] bg-white text-center text-sm font-medium outline-none',
                'shadow-[0px_1px_2px_rgba(10,13,18,0.05),_inset_0px_0px_0px_1px_rgba(10,13,18,0.18),_inset_0px_-2px_0px_rgba(10,13,18,0.05)]',
                'focus:ring-2 focus:ring-neutral-200 disabled:opacity-40'
              )}
              disabled={disabled}
            />
          )}
          <span className='text-sm text-neutral-700'>of {totalPages}</span>
          {clampedPage < totalPages && (
            <ArrowButton
              text='Next'
              direction='right'
              disabled={!canNext}
              onClick={() => go(clampedPage + 1)}
              className={cn(
                'w-auto px-3',
                !canNext && 'cursor-not-allowed opacity-40'
              )}
            />
          )}
        </div>
      </Flex>
    </nav>
  )
}

export default TablePaginate
